import { EditOutlined } from '@ant-design/icons';
import {
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Form,
  Grid,
  Input,
  Modal,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EasterEggDiploma,
  markDiplomaShown,
  shouldShowDiploma,
} from '../components/EasterEggDiploma';
import api from '../config/api';
import { localeTag } from '../i18n/locale';
import {
  REGISTRATION_STATUSES,
  type RegistrationStatus,
  registrationStatusLabelKey,
} from '../i18n/registrationStatus';
import { useAuthStore } from '../store/authStore';
import { registrationTicketTypes } from '../utils/ticketTypeVisibility';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

/** Egy egyedi mező definíciója az esemény config-jából. */
interface CustomFieldDef {
  key: string;
  label?: string;
  type?: string;
  scope?: string;
  options?: { value: string; label: string }[];
}

/**
 * A config-ot a backend validáció nélkül tárolja (`@Body() body: any`), ezért
 * defenzíven olvassuk: kulcs nélküli vagy nem objektum definíciót kihagyunk,
 * különben egyetlen hibás sor kiütné a jelentkezés részleteit.
 */
const customFieldDefs = (raw: unknown, scope: string): CustomFieldDef[] =>
  Array.isArray(raw)
    ? (raw as CustomFieldDef[]).filter(
        (f) =>
          f &&
          typeof f === 'object' &&
          typeof f.key === 'string' &&
          f.key !== '' &&
          f.scope === scope
      )
    : [];

/**
 * A tárolt érték a gépi kulcs (`omada`), a szervezőnek viszont a feliratot
 * kell látnia (`TP-Link Omada`). Ha az opció már nincs a config-ban — mert
 * időközben átírták —, a nyers értéket mutatjuk, hogy ne tűnjön el az adat.
 */
const customFieldValue = (
  def: CustomFieldDef,
  values: Record<string, string> | null | undefined
): string => {
  const raw = values && typeof values === 'object' ? values[def.key] : undefined;
  if (raw === undefined || raw === null || raw === '') return '—';
  if (def.type === 'select' && Array.isArray(def.options)) {
    const hit = def.options.find((o) => o && o.value === raw);
    if (hit?.label) return hit.label;
  }
  return String(raw);
};

interface Registration {
  id: string;
  eventId: string;
  companyId?: string | null;
  companyName: string;
  companyTaxNumber: string;
  companyCity?: string;
  companyPostalCode?: string;
  companyAddress?: string;
  companyStreetAddress?: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone?: string;
  status: RegistrationStatus;
  priceFlags?: Record<string, boolean>;
  // Az esemény config-jában `scope: 'registration'`-ként definiált egyedi
  // mezők értékei. A résztvevőnkéntiek a Ticket soraiban vannak.
  customFields?: Record<string, string> | null;
  calculatedPrice?: number;
  currency?: string;
  paymentDeadline?: string;
  createdAt: string;
  Event?: { id: string; title: string; slug: string };
  Ticket?: {
    id: string;
    firstName: string;
    lastName: string;
    jobTitle?: string;
    personalEmail?: string;
    ticketNumber: string;
    ticketTypeId?: string;
    customFields?: Record<string, string> | null;
    TicketType?: { id: string; name: string; price: number };
  }[];
}

interface TicketTypeOption {
  id: string;
  name: string;
  price: number;
  isPublic?: boolean;
}

interface EventOption {
  id: string;
  title: string;
}

/**
 * The human-readable label for a status, falling back to the raw enum value.
 *
 * The page used to print the enum itself — `PAYMENT_INSTRUCTIONS_SENT` in the
 * column, in the column filter and in the toolbar filter, in Hungarian and in
 * English alike. The fallback keeps that behaviour for a status this build does
 * not know, which is the only case where the raw value is still the honest
 * answer.
 */
const statusLabel = (t: TFunction, status: string): string => {
  const key = registrationStatusLabelKey(status);
  return key ? t(key) : status;
};

const STATUS_COLORS: Record<RegistrationStatus, string> = {
  REGISTERED: 'blue',
  APPROVED: 'cyan',
  TICKET_REQUESTED: 'purple',
  PAYMENT_INSTRUCTIONS_SENT: 'orange',
  PAID: 'green',
  PAYMENT_EXPIRED: 'red',
  CANCELLED: 'default',
};

// Két export, két kérdésre: a `paid` a könyvelőé (fizetett jelentkezések,
// számlázási adatok), az `attendees` a szervezőé (résztvevőnként egy sor, az
// esemény egyedi mezőivel — étkezési igény, break-out választás; #196).
const EXPORTS = {
  paid: { path: '/registrations/export.csv', filePrefix: 'paid-registrations' },
  attendees: { path: '/registrations/attendees.csv', filePrefix: 'attendees' },
} as const;

const Registrations: React.FC = () => {
  const { t, i18n } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  // A statikus `Modal.confirm` a React-fán kívül rajzol, ezért nem látja a
  // `ConfigProvider` témáját: antd-kék gombbal, alap betűvel jött a piros,
  // Geist-es panel mellé (#145). A context-tudatos példány az `App` alól jön.
  const { modal } = AntdApp.useApp();
  const [items, setItems] = useState<Registration[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [eventId, setEventId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<RegistrationStatus | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [events, setEvents] = useState<EventOption[]>([]);
  const [detail, setDetail] = useState<Registration | null>(null);
  const [ticketTypes, setTicketTypes] = useState<TicketTypeOption[]>([]);
  const [diplomaOpen, setDiplomaOpen] = useState(false);
  const [exporting, setExporting] = useState<'paid' | 'attendees' | null>(null);
  const [deleting, setDeleting] = useState(false);
  const authUser = useAuthStore((s) => s.user);

  // A törlés jogosultsága tükrözze a backend @Roles listáját a
  // DELETE /registrations/:id végponton — különben a gomb aktívnak látszik,
  // de a kérés 403-mal elszáll.
  const canDelete = ['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'EVENT_ADMIN'].includes(
    authUser?.role || ''
  );

  const handleExportCsv = async (kind: keyof typeof EXPORTS) => {
    const { path, filePrefix } = EXPORTS[kind];
    setExporting(kind);
    try {
      const resp = await api.get(path, {
        params: eventId ? { eventId } : undefined,
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `${filePrefix}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success(t('registrations.csvDownloaded'));
    } catch (e: any) {
      message.error(e.response?.data?.message ?? t('registrations.exportFailed'));
    } finally {
      setExporting(null);
    }
  };
  const [ticketTypeSaving, setTicketTypeSaving] = useState<string | null>(null);
  const [eventConfig, setEventConfig] = useState<any>(null);
  const [approveForm] = Form.useForm();
  const [companyEditOpen, setCompanyEditOpen] = useState(false);
  const [companyEditSaving, setCompanyEditSaving] = useState(false);
  const [companyEditForm] = Form.useForm();

  // Jelentkezés felvitele a panelről (#139). A jegytípus-lista az űrlapon
  // választott eseményé, nem a részletek modáljáé — a kettő nem ugyanaz az
  // esemény, ezért külön state.
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createForm] = Form.useForm();
  const [createTicketTypes, setCreateTicketTypes] = useState<TicketTypeOption[]>([]);
  const [createTicketTypesLoading, setCreateTicketTypesLoading] = useState(false);
  // Types exist but every one is admin-only: a different fix than "create one".
  const [createOnlyHiddenTypes, setCreateOnlyHiddenTypes] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    fetchAll();
  }, [eventId, status, page, search]);

  const fetchEvents = async () => {
    try {
      const r = await api.get('/events', { params: { limit: 100 } });
      // Backend returns { data: [...] } envelope; tolerate older shapes too.
      const payload = r.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.events)
            ? payload.events
            : [];
      setEvents(list);
    } catch {
      message.error(t('registrations.eventsLoadFailed'));
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get('/registrations', {
        params: { eventId, status, page, pageSize, search: search || undefined },
      });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch {
      message.error(t('registrations.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (reg: Registration) => {
    try {
      const r = await api.get(`/registrations/${reg.id}`);
      setDetail(r.data);
      if (reg.eventId) {
        const [c, tt] = await Promise.all([
          api.get(`/events/${reg.eventId}/config`),
          api.get(`/tickets/types/event/${reg.eventId}`),
        ]);
        setEventConfig(c.data);
        const types = Array.isArray(tt.data) ? tt.data : (tt.data?.items ?? []);
        setTicketTypes(registrationTicketTypes(types));
      }
      approveForm.setFieldsValue({ priceFlags: r.data.priceFlags ?? {} });
    } catch {
      message.error(t('registrations.detailLoadFailed'));
    }
  };

  const reloadDetail = async (id: string) => {
    const refreshed = await api.get(`/registrations/${id}`);
    setDetail(refreshed.data);
  };

  const closeDetail = () => {
    setDetail(null);
    setEventConfig(null);
    setTicketTypes([]);
  };

  const submitCompanyEdit = async () => {
    if (!detail) return;
    try {
      const values = await companyEditForm.validateFields();
      setCompanyEditSaving(true);
      await api.patch(`/registrations/${detail.id}/company`, values);
      message.success(
        values.syncCompany
          ? t('registrations.companyUpdatedWithSync')
          : t('registrations.companyUpdated')
      );
      await reloadDetail(detail.id);
      // A táblázat is felfrissül, hogy a lista a friss cégnevet mutassa
      fetchAll();
      setCompanyEditOpen(false);
    } catch (e: any) {
      if (e?.errorFields) return; // form validation error
      message.error(e?.response?.data?.message ?? t('registrations.saveFailed'));
    } finally {
      setCompanyEditSaving(false);
    }
  };

  const handleTicketTypeChange = async (ticketId: string, ticketTypeId: string) => {
    setTicketTypeSaving(ticketId);
    try {
      await api.patch(`/tickets/${ticketId}`, { ticketTypeId });
      message.success(t('registrations.ticketTypeUpdated'));
      if (detail) await reloadDetail(detail.id);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? t('registrations.updateFailed'));
    } finally {
      setTicketTypeSaving(null);
    }
  };

  /** Inline mezőszerkesztés (név + e-mail) a jegyek táblában. */
  const handleTicketFieldSave = async (
    ticketId: string,
    field: 'firstName' | 'lastName' | 'personalEmail' | 'jobTitle',
    value: string
  ) => {
    try {
      await api.patch(`/tickets/${ticketId}`, { [field]: value });
      message.success(t('registrations.updated'));
      if (detail) await reloadDetail(detail.id);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message?.message ??
        e?.response?.data?.message ??
        t('registrations.updateFailed');
      message.error(typeof msg === 'string' ? msg : t('registrations.updateFailed'));
    }
  };

  // Egy gombnyomással: jóváhagyás + fizetési instrukciók kiküldése.
  // REGISTERED → approve → payment-instructions
  // APPROVED   → csak payment-instructions
  const handleApproveAndSendPayment = async () => {
    if (!detail) return;
    try {
      if (detail.status === 'REGISTERED') {
        // silent: true → ne menjen ki külön "jóváhagyva" e-mail; a következő
        // payment-instructions levél úgyis tartalmazza a jóváhagyás tényét.
        await api.post(`/registrations/${detail.id}/approve`, {
          priceFlags: approveForm.getFieldValue('priceFlags'),
          silent: true,
        });
      }
      await api.post(`/registrations/${detail.id}/payment-instructions`, {});
      message.success(t('registrations.approvedAndSent'));
      await fetchAll();
      await reloadDetail(detail.id);

      // Easter egg: az első Connect+Grow 2026 approve a jogosult felhasználóknak
      // megnyit egy hivatalos „oklevél" popupot.
      if (shouldShowDiploma(authUser?.email, detail.Event?.slug)) {
        setDiplomaOpen(true);
      }
    } catch (e: any) {
      message.error(e.response?.data?.message ?? t('common.operation_failed'));
      // The approve step may have gone through before the letter was refused;
      // with a stale status the next press would try to approve again.
      await fetchAll();
      await reloadDetail(detail.id).catch(() => {});
    }
  };

  const handleAction = async (
    action:
      | 'approve'
      | 'payment-instructions'
      | 'resend-payment-instructions'
      | 'mark-paid'
      | 'expire'
  ) => {
    if (!detail) return;
    const body =
      action === 'approve' ? { priceFlags: approveForm.getFieldValue('priceFlags') } : {};
    try {
      await api.post(`/registrations/${detail.id}/${action}`, body);
      message.success(t('common.operation_success'));
      await fetchAll();
      await reloadDetail(detail.id);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? t('common.operation_failed'));
    }
  };

  const handleDelete = async () => {
    if (!detail) return;
    setDeleting(true);
    try {
      await api.delete(`/registrations/${detail.id}`);
      message.success(t('registrations.deleted'));
      closeDetail();
      await fetchAll();
    } catch {
      // A hibaüzenetet az api response interceptor már kiírja.
    } finally {
      setDeleting(false);
    }
  };

  /** Az összeg úgy, ahogy az Ár oszlop is írja: szám a panel nyelvén + pénznem. */
  const formatAmount = (price: number, currency: string) =>
    `${price.toLocaleString(localeTag(i18n.language))} ${currency}`.trim();

  /** A backend hibaüzenete, ha sztring — különben a hívó tartaléka megy. */
  const apiErrorMessage = (e: unknown): string | undefined => {
    const m = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
    return typeof m === 'string' ? m : undefined;
  };

  const loadCreateTicketTypes = async (evId: string) => {
    setCreateTicketTypesLoading(true);
    createForm.setFieldValue('ticketTypeId', undefined);
    try {
      const r = await api.get(`/tickets/types/event/${evId}`);
      const all: TicketTypeOption[] = Array.isArray(r.data) ? r.data : [];
      const usable = registrationTicketTypes(all);
      setCreateTicketTypes(usable);
      setCreateOnlyHiddenTypes(all.length > 0 && usable.length === 0);
    } catch {
      setCreateTicketTypes([]);
      setCreateOnlyHiddenTypes(false);
    } finally {
      setCreateTicketTypesLoading(false);
    }
  };

  const closeCreate = () => {
    setCreateOpen(false);
    createForm.resetFields();
    setCreateTicketTypes([]);
    setCreateOnlyHiddenTypes(false);
  };

  // Egy kérdés, egy lépés — a megerősítés a LÉTREHOZÁS ELÉ került (#146). A
  // „Felvitel" gomb még nem ír a szerverre: megkérdezi, kinek és melyik jegyre
  // visszük fel a jelentkezést, és csak a „Felvitel és kiküldés" indítja el a
  // két hívást egymás után — a létrehozást, majd a MEGLÉVŐ kiküldés-végpontot,
  // ugyanazt, amit a „Jóváhagyás + fizetési info" gomb hív. A „Vissza"
  // érintetlenül hagyja az űrlapot, és egyetlen kérést sem indít.
  //
  // Összeget a kérdés szándékosan NEM ír: a végösszeget a szerver számolja a
  // létrehozáskor, tehát egy előtte futó párbeszéd csak tippelhetne. A
  // jegytípus nevét írjuk ki — azt a szervező választotta —, az összeget a
  // fizetési levél viszi.
  const submitCreate = async () => {
    let values: Record<string, unknown>;
    try {
      values = await createForm.validateFields();
    } catch {
      return;
    }

    const name = `${values.contactFirstName ?? ''} ${values.contactLastName ?? ''}`.trim();
    const ticketType = createTicketTypes.find((tt) => tt.id === values.ticketTypeId)?.name ?? '';

    modal.confirm({
      title: t('registrations.createSendTitle'),
      content: t('registrations.createSendDesc', {
        name,
        email: values.contactEmail,
        ticketType,
      }),
      okText: t('registrations.createSendOk'),
      cancelText: t('common.back'),
      onOk: async () => {
        setCreateSaving(true);
        let created: { registration: { id: string } };
        try {
          const r = await api.post('/registrations/admin', values);
          created = r.data;
        } catch (e: unknown) {
          // A sor NEM jött létre. Az űrlap nyitva marad a beírt értékekkel,
          // hogy a szervező javíthasson — a leggyakoribb ok a már felvitt
          // adószám, és azt csak az űrlapon lehet átírni.
          message.error(apiErrorMessage(e) ?? t('registrations.createFailed'));
          setCreateSaving(false);
          return;
        }
        setCreateSaving(false);
        closeCreate();

        try {
          await api.post(`/registrations/${created.registration.id}/payment-instructions`, {});
          message.success(t('registrations.createSent', { email: values.contactEmail }));
        } catch (e: unknown) {
          // A jelentkezés MEGVAN, csak a levél nem ment ki. Ezt ki kell
          // mondani: különben a szervező azt hiszi, semmi nem történt, és
          // ugyanazt még egyszer felviszi.
          const detail = apiErrorMessage(e);
          const held = t('registrations.createSentFailed');
          message.error(detail ? `${held} ${detail}` : held);
        }
        await fetchAll();
      },
    });
  };

  const columns: ColumnsType<Registration> = [
    {
      title: t('registrations.columnCompany'),
      dataIndex: 'companyName',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v || '—'}</Text>
          {r.companyTaxNumber && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {r.companyTaxNumber}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: t('registrations.columnContact'),
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>
            {r.contactFirstName} {r.contactLastName}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.contactEmail}
          </Text>
        </Space>
      ),
    },
    {
      title: t('registrations.columnEvent'),
      responsive: ['md'],
      render: (_, r) => r.Event?.title ?? r.eventId,
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      render: (s: RegistrationStatus) => <Tag color={STATUS_COLORS[s]}>{statusLabel(t, s)}</Tag>,
      filters: REGISTRATION_STATUSES.map((s) => ({ text: statusLabel(t, s), value: s })),
      // FIGYELEM: ez a szűrő a betöltött oldalon dolgozik, a fenti eszköztár
      // `Státusz szűrő`-je viszont a szerveren. A kettő ezért mást ad ugyanarra
      // a státuszra, amíg a lista lapozott — a címkézés ezt láthatóbbá teszi,
      // de nem ez okozza. Külön döntés, hogy melyik maradjon.
      onFilter: (val, record) => record.status === val,
    },
    {
      title: t('registrations.columnPrice'),
      responsive: ['lg'],
      render: (_, r) =>
        r.calculatedPrice ? formatAmount(r.calculatedPrice, r.currency ?? '') : '—',
    },
    {
      title: t('registrations.columnCreated'),
      dataIndex: 'createdAt',
      responsive: ['lg'],
      // ISO on purpose, in every language. The date columns here are scanned and
      // sorted rather than read as prose, and ISO is the one format that stays
      // both unambiguous and lexically sortable — unlike the localized dates on
      // the dashboard, which are read. Do not "align" the two.
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('registrations.columnActions'),
      render: (_, r) => (
        <Button size="small" onClick={() => openDetail(r)}>
          {t('registrations.openDetail')}
        </Button>
      ),
    },
  ];

  // Az egyedi mezők definíciói az esemény config-jából. A `registration`
  // scope a jelentkezéshez tartozik egyszer, az `attendee` résztvevőnként.
  const regCustomFields = customFieldDefs(eventConfig?.registration?.customFields, 'registration');
  const attendeeCustomFields = customFieldDefs(eventConfig?.registration?.customFields, 'attendee');

  return (
    <>
      <Card>
        {/* A cím sorában, jobbra zárva — ahogy a panel többi listaoldala. Az
            oszlopok szándékosan span nélküliek (az Események oldal alakja):
            böngészőben mérve az `xs={24} md="auto"` páros az antd 5-ben NEM ad
            md-osztályt, tehát mindkét oszlop teljes szélességű marad, és a
            gombok a cím alá törnek — a Jegyek oldalon ma is így áll. Keskeny
            kijelzőn a sor magától törik, és a gombok balra zárnak. */}
        <Row
          justify="space-between"
          align="middle"
          gutter={[12, 12]}
          wrap
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              {t('registrations.title')}
            </Title>
          </Col>
          <Col>
            <Space
              wrap
              size={[8, 8]}
              style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}
            >
              <Button onClick={() => handleExportCsv('paid')} loading={exporting === 'paid'}>
                {t('registrations.exportCsv')}
              </Button>
              {/* Az oszlopokat egyetlen esemény config-ja adja, ezért a backend
                  `eventId` nélkül 400-at ad — a gomb addig letiltott, és
                  megmondja, miért. A letiltott gomb nem kap egérműveletet,
                  ezért a tooltip a körbeölelő span-en él. */}
              <Tooltip
                title={
                  eventId
                    ? t('registrations.exportAttendeesHint')
                    : t('registrations.exportAttendeesNeedsEvent')
                }
              >
                <span>
                  <Button
                    onClick={() => handleExportCsv('attendees')}
                    loading={exporting === 'attendees'}
                    disabled={!eventId}
                  >
                    {t('registrations.exportAttendees')}
                  </Button>
                </span>
              </Tooltip>
              <Button
                type="primary"
                onClick={() => {
                  setCreateOpen(true);
                  // A szűrőben választott esemény jó alapértelmezés; a
                  // jegytípusait rögtön betöltjük.
                  if (eventId) {
                    createForm.setFieldValue('eventId', eventId);
                    void loadCreateTicketTypes(eventId);
                  }
                }}
              >
                {t('registrations.newButton')}
              </Button>
            </Space>
          </Col>
        </Row>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
          <Col xs={12} md="auto">
            <Statistic title={t('registrations.totalLabel')} value={total} />
          </Col>
          <Col xs={24} md="auto" flex="auto">
            <Space wrap size={[8, 8]} style={{ width: '100%' }}>
              <Select
                style={{ minWidth: isMobile ? 140 : 200, width: isMobile ? '100%' : undefined }}
                placeholder={t('registrations.filterEvent')}
                allowClear
                value={eventId}
                onChange={(v) => {
                  setEventId(v);
                  setPage(1);
                }}
                options={events.map((e) => ({ label: e.title, value: e.id }))}
              />
              <Select
                style={{ minWidth: isMobile ? 140 : 200, width: isMobile ? '100%' : undefined }}
                placeholder={t('registrations.filterStatus')}
                allowClear
                value={status}
                onChange={(v) => {
                  setStatus(v as RegistrationStatus | undefined);
                  setPage(1);
                }}
                options={REGISTRATION_STATUSES.map((s) => ({
                  label: statusLabel(t, s),
                  value: s,
                }))}
              />
              <Input.Search
                placeholder={t('registrations.searchPlaceholder')}
                allowClear
                onSearch={(v) => {
                  setSearch(v);
                  setPage(1);
                }}
                style={{ width: isMobile ? '100%' : 280 }}
              />
            </Space>
          </Col>
        </Row>
        <Table
          rowKey="id"
          dataSource={items}
          columns={columns}
          loading={loading}
          scroll={{ x: 'max-content' }}
          size={isMobile ? 'small' : 'middle'}
          pagination={{
            current: page,
            pageSize,
            total,
            simple: isMobile,
            onChange: (p) => setPage(p),
          }}
        />
      </Card>

      <Modal
        open={!!detail}
        onCancel={closeDetail}
        title={
          detail
            ? t('registrations.detailTitle', {
                name: detail.companyName || detail.contactEmail,
              })
            : ''
        }
        footer={null}
        width={900}
      >
        {detail && (
          <>
            <Descriptions
              bordered
              size="small"
              column={isMobile ? 1 : 2}
              layout={isMobile ? 'vertical' : 'horizontal'}
              title={t('registrations.companyData')}
              extra={
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    companyEditForm.setFieldsValue({
                      companyName: detail.companyName,
                      companyTaxNumber: detail.companyTaxNumber,
                      companyPostalCode: detail.companyPostalCode,
                      companyCity: detail.companyCity,
                      companyAddress: detail.companyAddress,
                      companyStreetAddress: detail.companyStreetAddress,
                      contactFirstName: detail.contactFirstName,
                      contactLastName: detail.contactLastName,
                      contactEmail: detail.contactEmail,
                      contactPhone: detail.contactPhone,
                      syncCompany: false,
                    });
                    setCompanyEditOpen(true);
                  }}
                >
                  {t('registrations.editCompany')}
                </Button>
              }
            >
              <Descriptions.Item label={t('registrations.labelCompany')}>
                {detail.companyName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('registrations.labelTaxNumber')}>
                {detail.companyTaxNumber || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('registrations.labelPostalCode')}>
                {detail.companyPostalCode || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('registrations.labelCity')}>
                {detail.companyCity || detail.companyAddress || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('registrations.labelStreet')} span={2}>
                {detail.companyStreetAddress || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('registrations.labelContact')}>
                {detail.contactFirstName} {detail.contactLastName}
              </Descriptions.Item>
              <Descriptions.Item label={t('registrations.labelEmail')}>
                {detail.contactEmail}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.phone')}>
                {detail.contactPhone || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.status')}>
                <Tag color={STATUS_COLORS[detail.status]}>{statusLabel(t, detail.status)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('registrations.columnPrice')}>
                {detail.calculatedPrice
                  ? `${detail.calculatedPrice} ${detail.currency ?? ''}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('registrations.labelPaymentDeadline')}>
                {detail.paymentDeadline ? dayjs(detail.paymentDeadline).format('YYYY-MM-DD') : '—'}
              </Descriptions.Item>
            </Descriptions>

            {regCustomFields.length > 0 && (
              <Card style={{ marginTop: 16 }} title={t('registrations.customFields')}>
                <Descriptions
                  bordered
                  size="small"
                  column={isMobile ? 1 : 2}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                >
                  {regCustomFields.map((def) => (
                    <Descriptions.Item key={def.key} label={def.label || def.key}>
                      {customFieldValue(def, detail.customFields)}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </Card>
            )}

            {eventConfig?.pricing?.flags?.length > 0 && (
              <Card style={{ marginTop: 16 }} title={t('registrations.pricingFlags')}>
                <Form form={approveForm}>
                  {eventConfig.pricing.flags.map((flag: { key: string; label: string }) => (
                    <Form.Item
                      key={flag.key}
                      name={['priceFlags', flag.key]}
                      valuePropName="checked"
                    >
                      <Checkbox>{flag.label}</Checkbox>
                    </Form.Item>
                  ))}
                </Form>
              </Card>
            )}

            {detail.Ticket && detail.Ticket.length > 0 && (
              <Card
                style={{ marginTop: 16 }}
                title={t('registrations.requestedTickets', { n: detail.Ticket.length })}
              >
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={detail.Ticket}
                  scroll={{ x: 'max-content' }}
                  columns={[
                    {
                      title: t('registrations.ticketNumber'),
                      dataIndex: 'ticketNumber',
                      width: 200,
                    },
                    {
                      title: t('registrations.firstName'),
                      width: 140,
                      render: (_, ticket: any) => (
                        <Text
                          editable={{
                            tooltip: t('common.edit'),
                            onChange: (v) =>
                              v &&
                              v !== ticket.firstName &&
                              handleTicketFieldSave(ticket.id, 'firstName', v),
                          }}
                        >
                          {ticket.firstName ?? ''}
                        </Text>
                      ),
                    },
                    {
                      title: t('registrations.lastName'),
                      width: 140,
                      render: (_, ticket: any) => (
                        <Text
                          editable={{
                            tooltip: t('common.edit'),
                            onChange: (v) =>
                              v &&
                              v !== ticket.lastName &&
                              handleTicketFieldSave(ticket.id, 'lastName', v),
                          }}
                        >
                          {ticket.lastName ?? ''}
                        </Text>
                      ),
                    },
                    {
                      title: t('registrations.jobTitle'),
                      width: 160,
                      render: (_, ticket: any) => (
                        <Text
                          editable={{
                            tooltip: t('common.edit'),
                            onChange: (v) =>
                              v !== (ticket.jobTitle ?? '') &&
                              handleTicketFieldSave(ticket.id, 'jobTitle', v),
                          }}
                          type={ticket.jobTitle ? undefined : 'secondary'}
                        >
                          {ticket.jobTitle ?? '—'}
                        </Text>
                      ),
                    },
                    {
                      title: t('registrations.labelEmail'),
                      width: 240,
                      render: (_, ticket: any) => (
                        <Text
                          editable={{
                            tooltip: t('common.edit'),
                            onChange: (v) =>
                              v !== (ticket.personalEmail ?? '') &&
                              handleTicketFieldSave(ticket.id, 'personalEmail', v),
                          }}
                          style={{ wordBreak: 'break-all' }}
                        >
                          {ticket.personalEmail ?? ''}
                        </Text>
                      ),
                    },
                    {
                      title: t('registrations.ticketType'),
                      width: 260,
                      render: (_, ticket: any) => (
                        <Select
                          size="small"
                          style={{ width: 240 }}
                          placeholder={t('registrations.selectTicketType')}
                          value={ticket.ticketTypeId ?? undefined}
                          loading={ticketTypeSaving === ticket.id}
                          disabled={ticketTypeSaving === ticket.id || ticketTypes.length === 0}
                          onChange={(v) => handleTicketTypeChange(ticket.id, v)}
                          options={ticketTypes.map((tt) => ({
                            value: tt.id,
                            label: `${tt.price} — ${tt.name}`,
                          }))}
                        />
                      ),
                    },
                    // Résztvevőnkénti egyedi mezők — oszloponként egy, hogy a
                    // szervező végig tudja pásztázni (pl. ki melyik break-out
                    // szekciót választotta), ne soronként kelljen kinyitnia.
                    ...attendeeCustomFields.map((def) => ({
                      title: def.label || def.key,
                      width: 180,
                      render: (_: unknown, ticket: any) =>
                        customFieldValue(def, ticket.customFields),
                    })),
                  ]}
                />
              </Card>
            )}

            <Space style={{ marginTop: 16 }} wrap>
              {(() => {
                const canApproveAndSend =
                  detail.status === 'REGISTERED' ||
                  (detail.status === 'APPROVED' && (detail.Ticket?.length ?? 0) > 0) ||
                  detail.status === 'TICKET_REQUESTED';
                const canMarkPaid = ['PAYMENT_INSTRUCTIONS_SENT', 'PAYMENT_EXPIRED'].includes(
                  detail.status
                );
                const canResend = ['PAYMENT_INSTRUCTIONS_SENT', 'PAYMENT_EXPIRED'].includes(
                  detail.status
                );
                const canExpire = detail.status === 'PAYMENT_INSTRUCTIONS_SENT';
                return (
                  <>
                    <Popconfirm
                      title={t('registrations.approveConfirmTitle')}
                      description={t('registrations.approveConfirmDesc')}
                      onConfirm={handleApproveAndSendPayment}
                      disabled={!canApproveAndSend}
                    >
                      <Button type="primary" disabled={!canApproveAndSend}>
                        {t('registrations.approveButton')}
                      </Button>
                    </Popconfirm>
                    <Popconfirm
                      title={t('registrations.resendConfirmTitle')}
                      description={t('registrations.resendConfirmDesc')}
                      onConfirm={() => handleAction('resend-payment-instructions')}
                      disabled={!canResend}
                    >
                      {/* Letiltva a hét állapotból ötben, és a tanács nem
                          ugyanaz: amíg a „Jóváhagyás + fizetési info" küld,
                          arra mutatunk; ahol az is letiltott (fizetve,
                          lemondva, jegy nélkül), egy halott gombra mutatni
                          hazugság lenne. A szöveg ezért a `canApproveAndSend`-
                          ből jön, hogy a kettő ne csúszhasson el (#145). */}
                      <Tooltip
                        title={
                          canResend
                            ? ''
                            : canApproveAndSend
                              ? t('registrations.resendDisabledUseApprove', {
                                  button: t('registrations.approveButton'),
                                })
                              : t('registrations.resendDisabledNothingSent')
                        }
                      >
                        {/* A letiltott gomb nem kap egérműveletet, ezért a
                            tooltip a körbeölelő span-en él. */}
                        <span>
                          <Button disabled={!canResend}>{t('registrations.resendButton')}</Button>
                        </span>
                      </Tooltip>
                    </Popconfirm>
                    <Popconfirm
                      title={t('registrations.markPaidConfirmTitle')}
                      onConfirm={() => handleAction('mark-paid')}
                      disabled={!canMarkPaid}
                    >
                      <Button disabled={!canMarkPaid}>{t('registrations.markPaidButton')}</Button>
                    </Popconfirm>
                    <Popconfirm
                      title={t('registrations.expireConfirmTitle')}
                      onConfirm={() => handleAction('expire')}
                      disabled={!canExpire}
                    >
                      <Button danger disabled={!canExpire}>
                        {t('registrations.expireButton')}
                      </Button>
                    </Popconfirm>
                    <Popconfirm
                      title={t('registrations.deleteConfirmTitle')}
                      description={
                        detail.status === 'PAID'
                          ? t('registrations.deleteConfirmDescPaid')
                          : t('registrations.deleteConfirmDesc')
                      }
                      okText={t('common.delete')}
                      okButtonProps={{ danger: true, loading: deleting }}
                      onConfirm={handleDelete}
                      disabled={!canDelete}
                    >
                      <Tooltip title={canDelete ? '' : t('registrations.deleteNoPermission')}>
                        {/* A letiltott gomb nem kap egérműveletet, ezért a
                            tooltip a körbeölelő span-en él. */}
                        <span>
                          <Button danger type="primary" loading={deleting} disabled={!canDelete}>
                            {t('common.delete')}
                          </Button>
                        </span>
                      </Tooltip>
                    </Popconfirm>
                  </>
                );
              })()}
            </Space>
          </>
        )}
      </Modal>

      <Modal
        title={t('registrations.editCompany')}
        open={companyEditOpen}
        onCancel={() => setCompanyEditOpen(false)}
        onOk={submitCompanyEdit}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={companyEditSaving}
        destroyOnClose
        width={620}
      >
        <Form layout="vertical" form={companyEditForm}>
          <Form.Item
            label={t('registrations.fieldCompanyName')}
            name="companyName"
            rules={[{ required: true, message: t('registrations.fieldCompanyNameRequired') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('registrations.fieldTaxNumber')}
            name="companyTaxNumber"
            rules={[{ required: true, message: t('registrations.fieldTaxNumberRequired') }]}
          >
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} sm={8}>
              <Form.Item label={t('registrations.labelPostalCode')} name="companyPostalCode">
                <Input placeholder={t('registrations.placeholderPostalCode')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item label={t('registrations.labelCity')} name="companyCity">
                <Input placeholder={t('registrations.placeholderCity')} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={t('registrations.labelStreet')} name="companyStreetAddress">
            <Input placeholder={t('registrations.placeholderStreet')} />
          </Form.Item>

          {/* A fizetési instrukció a kapcsolattartó címére megy. Elgépelt cím
              esetén eddig sehol nem lehetett javítani, így az újraküldés
              minden alkalommal ugyanoda, a rossz címre ment (#65). */}
          <Divider orientation="left" plain style={{ marginTop: 4 }}>
            {t('registrations.contactSectionTitle')}
          </Divider>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('registrations.firstName')}
                name="contactFirstName"
                rules={[{ required: true, message: t('registrations.contactNameRequired') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('registrations.lastName')}
                name="contactLastName"
                rules={[{ required: true, message: t('registrations.contactNameRequired') }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={t('registrations.labelEmail')}
            name="contactEmail"
            extra={t('registrations.contactEmailExtra')}
            rules={[
              { required: true, message: t('registrations.contactEmailRequired') },
              { type: 'email', message: t('registrations.contactEmailInvalid') },
            ]}
          >
            <Input type="email" />
          </Form.Item>
          <Form.Item label={t('common.phone')} name="contactPhone">
            <Input />
          </Form.Item>

          {detail?.companyId && (
            <Form.Item
              name="syncCompany"
              valuePropName="checked"
              extra={t('registrations.syncCompanyExtra')}
            >
              <Checkbox>{t('registrations.syncCompanyLabel')}</Checkbox>
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={t('registrations.createTitle')}
        open={createOpen}
        onCancel={closeCreate}
        onOk={submitCreate}
        okText={t('registrations.createSubmit')}
        cancelText={t('common.cancel')}
        confirmLoading={createSaving}
        destroyOnClose
        width={620}
      >
        <Text type="secondary">{t('registrations.createIntro')}</Text>
        <Form layout="vertical" form={createForm} style={{ marginTop: 12 }}>
          <Form.Item
            label={t('registrations.createEvent')}
            name="eventId"
            rules={[{ required: true, message: t('registrations.createEventRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('registrations.filterEvent')}
              options={events.map((e) => ({ label: e.title, value: e.id }))}
              onChange={(v: string) => void loadCreateTicketTypes(v)}
            />
          </Form.Item>
          <Form.Item
            label={t('registrations.createTicketType')}
            name="ticketTypeId"
            rules={[{ required: true, message: t('registrations.createTicketTypeRequired') }]}
            extra={
              createForm.getFieldValue('eventId') &&
              !createTicketTypesLoading &&
              createTicketTypes.length === 0
                ? t(
                    createOnlyHiddenTypes
                      ? 'registrations.createOnlyHiddenTicketTypes'
                      : 'registrations.createNoTicketTypes'
                  )
                : undefined
            }
          >
            <Select
              placeholder={t('registrations.selectTicketType')}
              loading={createTicketTypesLoading}
              disabled={createTicketTypes.length === 0}
              options={createTicketTypes.map((tt) => ({
                value: tt.id,
                label: `${tt.price} — ${tt.name}`,
              }))}
            />
          </Form.Item>

          <Divider orientation="left" plain style={{ marginTop: 4 }}>
            {t('registrations.contactSectionTitle')}
          </Divider>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('registrations.firstName')}
                name="contactFirstName"
                rules={[{ required: true, message: t('registrations.contactNameRequired') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('registrations.lastName')}
                name="contactLastName"
                rules={[{ required: true, message: t('registrations.contactNameRequired') }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={t('registrations.labelEmail')}
            name="contactEmail"
            extra={t('registrations.contactEmailExtra')}
            rules={[
              { required: true, message: t('registrations.contactEmailRequired') },
              { type: 'email', message: t('registrations.contactEmailInvalid') },
            ]}
          >
            <Input type="email" />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label={t('common.phone')} name="contactPhone">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={t('registrations.jobTitle')} name="jobTitle">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain style={{ marginTop: 4 }}>
            {t('registrations.companyData')}
          </Divider>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label={t('registrations.fieldCompanyName')} name="companyName">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={t('registrations.fieldTaxNumber')} name="companyTaxNumber">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={t('registrations.createNotes')} name="notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <EasterEggDiploma
        open={diplomaOpen}
        recipientName={
          [authUser?.firstName, authUser?.lastName].filter(Boolean).join(' ') ||
          authUser?.email ||
          t('registrations.diplomaFallbackName')
        }
        onClose={() => {
          if (authUser?.email) markDiplomaShown(authUser.email);
          setDiplomaOpen(false);
        }}
      />
    </>
  );
};

export default Registrations;
